import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Trophy, Sparkles, Award, BookOpen, Users, Lock, CheckCircle2, Loader2,
    Flame, Medal, Droplet, Sprout,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import {
    fetchProgress, fetchCurriculum, fetchJoinedClasses, joinClass, fetchMyRank,
    updateSettings, fetchMyPicks, deletePick, fetchMyClassPlans,
} from "@/lib/api";
import { getBadges, ALL_WEEKS } from "@/lib/badges";
import { MD } from "@/components/MD";
import PlanComments from "@/components/PlanComments";

function formatErr(detail) {
    if (!detail) return "";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
}

export default function StudentDashboard() {
    const { lang, t } = useLang();
    const { user, loading: authLoading, refresh } = useAuth();
    const nav = useNavigate();
    const [progress, setProgress] = useState([]);
    const [weeks, setWeeks] = useState([]);
    const [classes, setClasses] = useState([]);
    const [classRanks, setClassRanks] = useState({});  // class_id -> {rank, total, streak}
    const [picks, setPicks] = useState([]);
    const [openPick, setOpenPick] = useState(null);
    const [classPlans, setClassPlans] = useState([]);
    const [openPlan, setOpenPlan] = useState(null);
    const [loading, setLoading] = useState(true);

    // Join class form
    const [code, setCode] = useState("");
    const [joining, setJoining] = useState(false);
    const [joinMsg, setJoinMsg] = useState(null);

    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            nav("/login", { replace: true });
            return;
        }
        Promise.all([
            fetchProgress(),
            fetchCurriculum(lang),
            fetchJoinedClasses(),
            fetchMyPicks().catch(() => []),
            fetchMyClassPlans().catch(() => []),
        ])
            .then(async ([p, w, c, pk, cp]) => {
                setProgress(p);
                setWeeks(w);
                setClasses(c);
                setPicks(pk || []);
                setClassPlans(cp || []);
                // Fetch rank for every joined class in parallel
                const ranks = await Promise.all(
                    c.map((cls) =>
                        fetchMyRank(cls.id).then(
                            (r) => ({ id: cls.id, ...r }),
                            () => null,
                        ),
                    ),
                );
                const dict = {};
                ranks.filter(Boolean).forEach((r) => (dict[r.id] = r));
                setClassRanks(dict);
            })
            .finally(() => setLoading(false));
    }, [user, authLoading, lang, nav]);

    const submitJoin = async (e) => {
        e.preventDefault();
        const c = code.trim().toUpperCase();
        if (!c) return;
        setJoining(true);
        setJoinMsg(null);
        try {
            const res = await joinClass(c);
            setJoinMsg({ ok: true, name: res.class?.name });
            setCode("");
            const cls = await fetchJoinedClasses();
            setClasses(cls);
        } catch (err) {
            setJoinMsg({
                ok: false,
                msg: formatErr(err.response?.data?.detail) || t.student.invalid,
            });
        } finally {
            setJoining(false);
        }
    };

    const badges = getBadges(progress, lang);
    const earnedCount = badges.filter((b) => b.earned).length;
    const totalScore = progress.reduce(
        (acc, p) => acc + (p.best_score || 0),
        0,
    );
    const progressByWeek = Object.fromEntries(
        progress.map((p) => [p.week_id, p]),
    );

    return (
        <main
            className="max-w-7xl mx-auto px-5 md:px-8 pt-10 md:pt-16 pb-24 relative"
            data-testid="student-dashboard"
        >
            <div className="glow glow-lime -top-20 -left-20" />
            <div className="relative z-10">
                <span className="tag">// student panel</span>
                <h1 className="font-display font-black text-white text-4xl md:text-6xl mt-3 uppercase leading-[0.95]">
                    {t.student.head}
                </h1>
                {user && (
                    <p className="font-mono text-sm text-[var(--muted)] mt-2">
                        {user.name} · {user.email}
                    </p>
                )}
                <p className="font-mono text-[var(--muted)] mt-2">
                    {t.student.blurb}
                </p>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mt-10">
                    <Stat
                        Icon={BookOpen}
                        label={t.student.weeksDone}
                        value={`${progress.length}/6`}
                        color="var(--lime)"
                    />
                    <Stat
                        Icon={Trophy}
                        label={t.student.totalScore}
                        value={totalScore}
                        color="var(--cyan)"
                    />
                    <Stat
                        Icon={Award}
                        label={t.student.badgesEarned}
                        value={`${earnedCount}/${badges.length}`}
                        color="var(--coral)"
                    />
                </div>

                {/* Tools quick links */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-8">
                    <Link
                        to="/water-lab"
                        className="card p-4 hover:border-[var(--cyan)]"
                        data-testid="tool-water-lab"
                    >
                        <Droplet size={18} className="text-[var(--cyan)]" />
                        <div className="font-display font-bold text-white mt-2 text-sm">
                            {lang === "es" ? "Lab de Agua" : "Water Lab"}
                        </div>
                        <p className="font-mono text-[10px] text-[var(--muted)] mt-1">
                            {lang === "es"
                                ? "Registra lecturas, diagnóstico IA."
                                : "Log readings, get AI diagnostics."}
                        </p>
                    </Link>
                    <Link
                        to="/plant-picker"
                        className="card p-4 hover:border-[var(--lime)]"
                        data-testid="tool-plant-picker"
                    >
                        <Sprout size={18} className="text-[var(--lime)]" />
                        <div className="font-display font-bold text-white mt-2 text-sm">
                            {lang === "es" ? "Elegir Plantas" : "Plant Picker"}
                        </div>
                        <p className="font-mono text-[10px] text-[var(--muted)] mt-1">
                            {lang === "es"
                                ? "Las 3 ideales para tu sitio."
                                : "The 3 best plants for your spot."}
                        </p>
                    </Link>
                    <Link
                        to="/grow-lab"
                        className="card p-4 hover:border-[var(--coral)]"
                        data-testid="tool-grow-lab"
                    >
                        <Sparkles size={18} className="text-[var(--coral)]" />
                        <div className="font-display font-bold text-white mt-2 text-sm">
                            {lang === "es" ? "Lab de Cultivo" : "Grow Lab"}
                        </div>
                        <p className="font-mono text-[10px] text-[var(--muted)] mt-1">
                            {lang === "es"
                                ? "pH, nutrientes, sustratos, suelo."
                                : "pH, nutrients, media, soil."}
                        </p>
                    </Link>
                </div>

                {/* Opt-in leaderboard */}
                <div
                    className="card p-5 mt-6 flex items-center justify-between gap-3 flex-wrap"
                    data-testid="leaderboard-optin"
                >
                    <div className="flex items-center gap-3 min-w-[200px]">
                        <Trophy size={18} className="text-[var(--lime)]" />
                        <div>
                            <div className="font-display font-bold text-white text-sm">
                                {lang === "es"
                                    ? "Aparecer en el ranking público"
                                    : "Show me on the public leaderboard"}
                            </div>
                            <p className="font-mono text-[10px] text-[var(--muted)] mt-1 max-w-md">
                                {lang === "es"
                                    ? "Solo tu nombre, racha, semanas y puntaje. Apágalo cuando quieras."
                                    : "Just your name, streak, weeks done, and score. Toggle anytime."}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={async () => {
                            const next = !user.leaderboard_opt_in;
                            await updateSettings({ leaderboard_opt_in: next });
                            await refresh();
                        }}
                        data-testid="leaderboard-optin-toggle"
                        className={
                            "relative w-14 h-7 border-2 transition-colors " +
                            (user.leaderboard_opt_in
                                ? "border-[var(--lime)] bg-[var(--lime)]/20"
                                : "border-[var(--border)] bg-black")
                        }
                        aria-pressed={!!user.leaderboard_opt_in}
                    >
                        <span
                            className={
                                "absolute top-0.5 w-5 h-5 transition-all " +
                                (user.leaderboard_opt_in
                                    ? "left-7 bg-[var(--lime)]"
                                    : "left-0.5 bg-[var(--muted)]")
                            }
                        />
                    </button>
                </div>

                {loading ? (
                    <p className="font-mono text-[var(--muted)] mt-10">
                        {t.common.loading}
                    </p>
                ) : (
                    <>
                        {/* Weeks grid */}
                        <h2 className="font-display font-bold text-[var(--lime)] uppercase text-xl tracking-widest mt-14 mb-5">
                            {t.student.yourWeeks}
                        </h2>
                        <div
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                            data-testid="weeks-grid"
                        >
                            {weeks.map((w) => {
                                const p = progressByWeek[w.id];
                                const done = !!p;
                                const perfect = p && p.best_score === p.total;
                                return (
                                    <Link
                                        to={`/curriculum/${w.id}`}
                                        key={w.id}
                                        data-testid={`week-progress-${w.id}`}
                                        className={
                                            "card p-5 group transition-colors " +
                                            (perfect
                                                ? "border-[var(--lime)]"
                                                : done
                                                  ? "border-[var(--cyan)]"
                                                  : "")
                                        }
                                    >
                                        <div className="flex items-center justify-between">
                                            <span
                                                className="font-mono text-[10px] uppercase tracking-[0.22em]"
                                                style={{
                                                    color: perfect
                                                        ? "var(--lime)"
                                                        : done
                                                          ? "var(--cyan)"
                                                          : "var(--muted)",
                                                }}
                                            >
                                                W//0{w.week}
                                            </span>
                                            {done ? (
                                                <CheckCircle2
                                                    size={18}
                                                    style={{
                                                        color: perfect
                                                            ? "var(--lime)"
                                                            : "var(--cyan)",
                                                    }}
                                                />
                                            ) : (
                                                <Lock
                                                    size={16}
                                                    className="text-[var(--muted)]"
                                                />
                                            )}
                                        </div>
                                        <div className="font-display font-bold text-white text-lg leading-snug mt-2">
                                            {w.title}
                                        </div>
                                        <p className="font-mono text-xs text-[var(--muted)] mt-2">
                                            {w.tagline}
                                        </p>
                                        <div className="flex items-center justify-between mt-4">
                                            {done ? (
                                                <span
                                                    className="font-mono text-xs"
                                                    style={{
                                                        color: perfect
                                                            ? "var(--lime)"
                                                            : "var(--cyan)",
                                                    }}
                                                >
                                                    {t.student.best}: {p.best_score}/
                                                    {p.total}
                                                </span>
                                            ) : (
                                                <span className="font-mono text-xs text-[var(--muted)]">
                                                    —
                                                </span>
                                            )}
                                            <span
                                                className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--lime)] group-hover:text-[var(--cyan)]"
                                            >
                                                {done
                                                    ? perfect
                                                        ? t.student.review
                                                        : t.student.retake
                                                    : t.student.start}{" "}
                                                →
                                            </span>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>

                        {/* Badges */}
                        <h2 className="font-display font-bold text-[var(--coral)] uppercase text-xl tracking-widest mt-14 mb-5">
                            {t.student.badges}
                        </h2>
                        <div
                            className="grid grid-cols-2 md:grid-cols-4 gap-3"
                            data-testid="badges-grid"
                        >
                            {badges.map((b) => (
                                <div
                                    key={b.id}
                                    data-testid={`badge-${b.id}-${b.earned ? "earned" : "locked"}`}
                                    className={
                                        "border p-4 text-center " +
                                        (b.earned
                                            ? "border-[var(--lime)] bg-[var(--lime)]/5"
                                            : "border-[var(--border)] opacity-50")
                                    }
                                >
                                    <div className="text-4xl">{b.emoji}</div>
                                    <div className="font-display font-bold text-white text-sm mt-2 uppercase">
                                        {b.name}
                                    </div>
                                    <div className="font-mono text-[10px] text-[var(--muted)] mt-1 leading-snug">
                                        {b.desc}
                                    </div>
                                    <div
                                        className="font-mono text-[9px] uppercase tracking-widest mt-2"
                                        style={{
                                            color: b.earned
                                                ? "var(--lime)"
                                                : "var(--muted)",
                                        }}
                                    >
                                        {b.earned ? t.student.earned : t.student.locked}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Class Plans (shared by teacher) */}
                        {classPlans.length > 0 && (
                            <>
                                <h2 className="font-display font-bold text-[var(--coral)] uppercase text-xl tracking-widest mt-14 mb-5 flex items-center gap-3">
                                    <BookOpen size={18} />
                                    {lang === "es"
                                        ? "Planes de la Clase"
                                        : "Class Plans"}
                                </h2>
                                <div className="space-y-3" data-testid="class-plans-list">
                                    {classPlans.map((p) => {
                                        const open = openPlan === p.id;
                                        return (
                                            <div
                                                key={p.id}
                                                className={
                                                    "card p-4 " +
                                                    (open
                                                        ? "border-[var(--coral)]"
                                                        : "")
                                                }
                                                data-testid={`class-plan-${p.id}`}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setOpenPlan(
                                                            open ? null : p.id,
                                                        )
                                                    }
                                                    className="w-full text-left"
                                                    data-testid={`class-plan-toggle-${p.id}`}
                                                >
                                                    <div className="font-display text-white text-base uppercase">
                                                        {p.topic}
                                                    </div>
                                                    <div className="font-mono text-[10px] text-[var(--muted)] mt-1">
                                                        {p.class_name} ·{" "}
                                                        {p.grade_level} ·{" "}
                                                        {p.duration_min} min ·{" "}
                                                        {new Date(
                                                            p.created_at,
                                                        ).toLocaleDateString()}
                                                    </div>
                                                </button>
                                                {open && (
                                                    <>
                                                        <div className="mt-4 pt-4 border-t border-[var(--border)]">
                                                            <MD text={p.plan_markdown} />
                                                        </div>
                                                        <PlanComments planId={p.id} testidPrefix="s-" />
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {/* My Grow Plans */}
                        {picks.length > 0 && (
                            <>
                                <h2 className="font-display font-bold text-[var(--lime)] uppercase text-xl tracking-widest mt-14 mb-5 flex items-center gap-3">
                                    <Sprout size={18} />
                                    {lang === "es" ? "Mis Planes de Cultivo" : "My Grow Plans"}
                                </h2>
                                <div
                                    className="grid grid-cols-1 md:grid-cols-2 gap-3"
                                    data-testid="my-picks-list"
                                >
                                    {picks.map((p) => {
                                        const open = openPick === p.id;
                                        const summary =
                                            (p.input?.goal || "")
                                                + " · " + (p.input?.space || "");
                                        return (
                                            <div
                                                key={p.id}
                                                className={
                                                    "card p-4 " +
                                                    (open ? "border-[var(--lime)]" : "")
                                                }
                                                data-testid={`pick-${p.id}`}
                                            >
                                                <div className="flex items-start justify-between gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setOpenPick(open ? null : p.id)
                                                        }
                                                        data-testid={`pick-toggle-${p.id}`}
                                                        className="flex-1 text-left"
                                                    >
                                                        <div className="font-display text-white text-sm uppercase">
                                                            {summary}
                                                        </div>
                                                        <div className="font-mono text-[10px] text-[var(--muted)] mt-1">
                                                            pH {p.input?.ph_range} ·{" "}
                                                            {p.input?.light} ·{" "}
                                                            {p.input?.time_to_harvest} ·{" "}
                                                            {new Date(p.created_at).toLocaleDateString()}
                                                        </div>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={async () => {
                                                            if (
                                                                !window.confirm(
                                                                    lang === "es"
                                                                        ? "¿Borrar este plan?"
                                                                        : "Delete this plan?",
                                                                )
                                                            )
                                                                return;
                                                            await deletePick(p.id);
                                                            setPicks((xs) =>
                                                                xs.filter((x) => x.id !== p.id),
                                                            );
                                                        }}
                                                        data-testid={`pick-del-${p.id}`}
                                                        className="text-[var(--muted)] hover:text-[var(--coral)]"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                                {open && (
                                                    <div className="mt-4 pt-4 border-t border-[var(--border)]">
                                                        <MD text={p.result_markdown} />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {/* Classes */}
                        <h2 className="font-display font-bold text-[var(--cyan)] uppercase text-xl tracking-widest mt-14 mb-5">
                            {t.student.myClasses}
                        </h2>

                        {classes.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
                                {classes.map((c) => {
                                    const r = classRanks[c.id];
                                    return (
                                        <div
                                            key={c.id}
                                            className="card p-4"
                                            data-testid={`joined-class-${c.id}`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <Users
                                                    size={16}
                                                    className="text-[var(--cyan)]"
                                                />
                                                <div className="font-display font-bold text-white">
                                                    {c.name}
                                                </div>
                                            </div>
                                            <div className="font-mono text-xs text-[var(--muted)] mt-2">
                                                {c.teacher_name} · code{" "}
                                                <span className="text-[var(--lime)] tracking-widest">
                                                    {c.code}
                                                </span>
                                            </div>
                                            {r && r.rank && (
                                                <div
                                                    className="flex items-center gap-4 mt-3 pt-3 border-t border-[var(--border)]"
                                                    data-testid={`rank-${c.id}`}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <Medal
                                                            size={14}
                                                            className={
                                                                r.rank === 1
                                                                    ? "text-[var(--lime)]"
                                                                    : r.rank <= 3
                                                                      ? "text-[var(--cyan)]"
                                                                      : "text-[var(--muted)]"
                                                            }
                                                        />
                                                        <span className="font-mono text-xs">
                                                            <span className="text-[var(--muted)]">
                                                                {t.student.classRank}:{" "}
                                                            </span>
                                                            <span className="text-white font-bold">
                                                                #{r.rank}
                                                            </span>
                                                            <span className="text-[var(--muted)]">
                                                                {" "}
                                                                {t.student.of} {r.total}
                                                            </span>
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <Flame
                                                            size={14}
                                                            className="text-[var(--coral)]"
                                                        />
                                                        <span className="font-mono text-xs text-white">
                                                            {r.streak ?? 0}{" "}
                                                            <span className="text-[var(--muted)]">
                                                                {t.student.streak}
                                                            </span>
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <form
                            onSubmit={submitJoin}
                            className="card p-5 flex items-end gap-3 flex-wrap"
                            data-testid="join-class-form"
                        >
                            <label className="flex-1 min-w-[200px]">
                                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--muted)]">
                                    {t.student.joinClassHead}
                                </span>
                                <input
                                    value={code}
                                    onChange={(e) =>
                                        setCode(e.target.value.toUpperCase())
                                    }
                                    placeholder={t.student.joinCodePh}
                                    className="input-line mt-1 tracking-widest"
                                    data-testid="join-class-code"
                                    maxLength={12}
                                />
                            </label>
                            <button
                                type="submit"
                                disabled={joining || !code.trim()}
                                className="btn-prime"
                                data-testid="join-class-submit"
                            >
                                {joining && (
                                    <Loader2 size={14} className="animate-spin" />
                                )}
                                {t.student.join}
                            </button>
                        </form>
                        {joinMsg && (
                            <p
                                className={
                                    "font-mono text-sm mt-3 " +
                                    (joinMsg.ok
                                        ? "text-[var(--lime)]"
                                        : "text-[var(--coral)]")
                                }
                                data-testid="join-class-msg"
                            >
                                {joinMsg.ok
                                    ? `${t.student.joined} ${joinMsg.name || ""}`
                                    : joinMsg.msg}
                            </p>
                        )}
                    </>
                )}
            </div>
        </main>
    );
}

function Stat({ Icon, label, value, color }) {
    return (
        <div className="card p-5">
            <Icon size={18} style={{ color }} />
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--muted)] mt-2">
                {label}
            </div>
            <div className="font-display font-black text-2xl md:text-3xl mt-1 text-white">
                {value}
            </div>
        </div>
    );
}
